import { Col, message, Row, Table, Typography } from "antd";
import React, { useMemo, useState } from "react";
import { useForm } from "react-hook-form";

import { useGetOrganizationById } from "../../../api/v1";
import { useOrganizationSelection } from "../../../hooks/useOrganizationSelection";
import { findSelectOption } from "../../../libs/utils";
import { Popup } from "../../atoms/Popup";
import SectionHeader from "../../molecules/SectionHeader";

import { EnablementFlagForm } from "./EnablementFlagForm";
import { getEnablementFlagsColumns } from "./tableColumns";
import { EnablementFlagFormData } from "./types";
import { useEnablementFlags } from "./useEnablementFlags";

const emptyFormValues: EnablementFlagFormData = {
  organization: null,
  flag: "",
  value: true,
};

export const EnablementFlagsModule = () => {
  const { orgId, organizations, organizationOptions, changeSelectedOrgId } = useOrganizationSelection();
  const { organization, isLoadingOrganization } = useGetOrganizationById({
    orgId,
    params: { retrieveModuleConfigurations: true },
  });
  const { flags, isLoadingFlags, pendingFlag, isAddingFlag, toggleFlag, addFlag } = useEnablementFlags({
    orgId,
    organization,
    isLoadingOrganization,
  });
  const [searchText, setSearchText] = useState("");
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);

  const methods = useForm<EnablementFlagFormData>({
    defaultValues: emptyFormValues,
  });

  const columns = getEnablementFlagsColumns(pendingFlag, toggleFlag);

  const filteredFlags = useMemo(() => {
    const term = searchText.trim().toLowerCase();
    if (!term) return flags;
    return flags.filter((def) => def.flag.toLowerCase().includes(term));
  }, [flags, searchText]);

  const closeAddModal = () => {
    setIsAddModalOpen(false);
    methods.reset(emptyFormValues);
  };

  const handleAdd = () => {
    if (!orgId) {
      message.warning("Select an organization");
      return;
    }

    setIsAddModalOpen(true);
    methods.reset({
      ...emptyFormValues,
      organization: findSelectOption(organizationOptions, orgId) ?? null,
      value: true,
    });
  };

  const handleSubmit = async (formData: EnablementFlagFormData) => {
    const success = await addFlag(formData.flag, formData.value);
    if (success) closeAddModal();
  };

  return (
    <>
      <SectionHeader
        heading="Enablement Flags"
        addNewAction={handleAdd}
        organizationList={organizations}
        setOrgId={changeSelectedOrgId}
        orgFilterInitialValue={{ value: orgId }}
        searchOptions={[{ value: "flag", label: "Flag" }]}
        searchingBy="Flag"
        setSearchBy={() => {}}
        setSearchText={setSearchText}
      />

      <Row gutter={20}>
        <Col span={24}>
          <Table
            rowKey={(record) => record.flag}
            className="custom-table org_table_wrap"
            columns={columns}
            dataSource={filteredFlags}
            loading={isLoadingFlags}
          />
        </Col>
      </Row>

      <Popup
        closable
        visibility={isAddModalOpen}
        title={<Typography.Title level={3}>Add Enablement Flag</Typography.Title>}
        content={
          <EnablementFlagForm
            methods={methods}
            organizationOptions={organizationOptions}
            isSubmitting={isAddingFlag}
            onSubmit={handleSubmit}
            onCancel={closeAddModal}
          />
        }
        width={520}
        onCancel={closeAddModal}
      />
    </>
  );
};
